import React, { useCallback, useEffect, useRef } from 'react';

export default function Particles({
  containerRef,
  styles,
  count = 300,
  primaryColor = '#07C799',
  secondaryColor = '#ff2059',
}) {
  const canvasRef = useRef();
  let primaryParticles = useRef([]);
  let secondaryParticles = useRef([]);
  let elapsedTime = 0;

  // Function to initialize particles
  const initializeParticles = (count, w, isPrimary) => {
    return [...Array(count)].map(() => ({
      x: isPrimary ? Math.random() * w : Math.random() * (w * 0.43 - w * 0.05) + w * 0.05,
      offset: Math.random() * count - count,
      speed: Math.random() * (1 - 0.25) + 0.25,
      multiplier: Math.random() * (1 - 0.25) + 0.25,
    }));
  };

  const draw = useCallback((particles, color, ctx, w, h, isPrimary) => {
    particles.forEach((particle, i) => {
      let x, y;

      if (isPrimary) {
        // If particle goes beyond canvas width, reset it to start
        x = particle.x > w ? 0 : particle.x;

        // Calculate y position with a cosine wave
        const waveFactor = Math.cos((x - w * 0.6) * 0.005);
        y = h * 0.1 - particle.offset + waveFactor * ((h + particle.offset) * particle.multiplier * 0.7);

        // Update particle's x position
        particle.x = x + particle.speed;
      } else {
        // Calculate x position with a sine wave
        x = particle.x + Math.sin(elapsedTime * particle.multiplier * 0.05) * (10 * particle.multiplier);

        // Calculate y position with a cosine wave
        const waveFactor = Math.cos((primaryParticles.current[i].x - w * 0.5) * 0.005);
        y = h * 0.4 + waveFactor * ((h + particle.offset) * particle.multiplier * 0.3);
      }

      // Draw the particle
      ctx.fillStyle = color;
      ctx.globalAlpha = particle.multiplier;
      ctx.beginPath();
      ctx.arc(x, y, particle.speed * 8, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
    });
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    // Clear the canvas
    ctx.clearRect(0, 0, w, h);

    // Draw particles
    draw(primaryParticles.current, primaryColor, ctx, w, h, true);
    draw(secondaryParticles.current, secondaryColor, ctx, w, h, false);

    // Increment elapsed time
    elapsedTime++;

    // Request next animation frame
    requestAnimationFrame(animate);
  }, [draw, elapsedTime, primaryColor, secondaryColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w = (canvas.width = containerRef.current.offsetWidth);
    let h = (canvas.height = containerRef.current.offsetHeight);

    // Initialize particles
    const initialize = () => {
      primaryParticles.current = initializeParticles(count, w, true);
      secondaryParticles.current = initializeParticles(count * 0.2, w);
    };

    initialize();
    animate();

    // Handle window resize
    const handleResize = () => {
      w = canvas.width = containerRef.current.offsetWidth;
      h = canvas.height = containerRef.current.offsetHeight;
      initialize();
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, primaryParticles, secondaryParticles, animate, draw]);

  return <canvas ref={canvasRef} className={styles.particlesCanvas} id="particles" />;
}
